/**
 * Modal pour l'import d'un brouillard de caisse via Excel (format CECA).
 * Ce fichier est NOUVEAU et n'affecte pas les composants existants.
 */
import React, { useState } from "react";
import {
  FaCheckCircle,
  FaDownload,
  FaExclamationTriangle,
  FaFileExcel,
  FaInfoCircle,
  FaTimes,
  FaUpload,
} from "react-icons/fa";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { useImportBrouillardExcelMutation } from "../../../actions/transactions/importBrouillardMutation";
import ExcelDropzone from "../../../components/FileUpload/ExcelDropzone";
import ImportResultsDisplay from "../../../components/ImportResults/ImportResultsDisplay";
import { generateBrouillardTemplate } from "../../../utils/excelTemplateBrouillard";
import "../../../pagesDossierClientCabinet/SaisieManuelle/ModalImportEntrees.css";

Modal.setAppElement("#root");

const ModalImportBrouillard = ({ isOpen, onClose, dossierId, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);

  const [importBrouillard, { isLoading: loading }] =
    useImportBrouillardExcelMutation();

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setResults(null);
  };

  const handleDownloadTemplate = () => {
    try {
      generateBrouillardTemplate();
      toast.success("Modèle téléchargé avec succès");
    } catch (error) {
      console.error("Erreur téléchargement modèle:", error);
      toast.error("Erreur lors du téléchargement du modèle");
    }
  };

  const normalizeResponse = (data = {}) => ({
    success: data.success ?? false,
    message: data.message ?? "",
    sorties_creees: data.sorties_creees ?? 0,
    entrees_creees: data.entrees_creees ?? 0,
    produits_crees: 0,
    parsing_errors: data.errors ?? [],
    lignes_ignorees: [],
    erreurs_creation: data.erreurs ?? [],
    erreurs: data.erreurs ?? [],
    aide: null,
  });

  const handleImport = async () => {
    if (!file || !dossierId) {
      setResults({
        success: false,
        message: !file
          ? "Veuillez sélectionner un fichier Excel."
          : "Identifiant du dossier manquant.",
        sorties_creees: 0,
        entrees_creees: 0,
        parsing_errors: [],
        erreurs_creation: [],
      });
      return;
    }

    setResults(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("dossier_id", dossierId);

      const response = await importBrouillard(formData).unwrap();
      const normalized = normalizeResponse(response);
      setResults(normalized);

      if (normalized.success) {
        setTimeout(() => {
          if (onSuccess) onSuccess();
        }, 1500);
      }
    } catch (error) {
      const apiData = error?.data || error?.error?.data || {};
      setResults(normalizeResponse(apiData));
    }
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    setShowInstructions(false);
    onClose();
  };

  const handleCloseWithRefresh = () => {
    if (results && (results.entrees_creees > 0 || results.sorties_creees > 0)) {
      if (onSuccess) onSuccess();
    }
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modal-import-entrees"
      overlayClassName="modal-import-overlay"
      contentLabel="Importer un brouillard de caisse"
    >
      <div className="modal-import-container">
        <div className="modal-import-header">
          <div className="header-content">
            <div className="header-icon">
              <FaFileExcel />
            </div>
            <div className="header-text">
              <h2>Importer un brouillard de caisse</h2>
              <p>
                Importez un journal de caisse (format CECA) : chaque ligne
                devient une entrée ou une sortie réglée en caisse
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close-modal"
            onClick={handleClose}
            disabled={loading}
          >
            <FaTimes />
          </button>
        </div>

        <div className="modal-import-content">
          {/* Section 1 : Modèle */}
          <div className="import-section">
            <div className="section-header">
              <div className="section-number">1</div>
              <h3>Télécharger le modèle Excel</h3>
            </div>
            <p className="section-description">
              Utilisez le modèle au format brouillard (DATE, REFERENCES,
              LIBELLES, ENTREES, SORTIES, SOLDES) ou votre propre fichier au
              même format.
            </p>
            <div className="section-actions">
              <button
                type="button"
                className="btn-download-template"
                onClick={handleDownloadTemplate}
                disabled={loading}
              >
                <FaDownload />
                <span>Télécharger le modèle</span>
              </button>
              <button
                type="button"
                className="btn-show-instructions"
                onClick={() => setShowInstructions(!showInstructions)}
              >
                <FaInfoCircle />
                <span>
                  {showInstructions ? "Masquer" : "Voir"} les instructions
                </span>
              </button>
            </div>

            {showInstructions && (
              <div className="instructions-panel">
                <h4>
                  <FaInfoCircle /> Instructions d&apos;import
                </h4>
                <div className="instructions-content">
                  {[
                    {
                      titre: "Format du fichier",
                      texte: "Une seule feuille, en-tête : DATE, REFERENCES, LIBELLES, ENTREES, SORTIES, SOLDES.",
                    },
                    {
                      titre: "Champs obligatoires",
                      texte: "DATE (JJ/MM/AAAA ou date Excel) et LIBELLES.",
                    },
                    {
                      titre: "Montants",
                      texte: "Renseignez ENTREES (montant encaissé) ou SORTIES (montant décaissé), jamais les deux sur la même ligne.",
                    },
                    {
                      titre: "SOLDES",
                      texte: "Colonne de formule du modèle, ignorée à l'import.",
                    },
                    {
                      titre: "Effet comptable",
                      texte: "Chaque ligne devient une entrée ou une sortie payée comptant en caisse (compte 571).",
                    },
                    {
                      titre: "Règle tout ou rien",
                      texte: "Si une seule ligne est invalide, aucune écriture n'est créée. Toutes les erreurs s'affichent ici.",
                    },
                  ].map(({ titre, texte }) => (
                    <div className="instruction-item" key={titre}>
                      <FaCheckCircle className="check-icon" />
                      <div>
                        <strong>{titre} :</strong>
                        <p>{texte}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Section 2 : Fichier */}
          {!results && (
            <div className="import-section">
              <div className="section-header">
                <div className="section-number">2</div>
                <h3>Sélectionner votre fichier</h3>
              </div>
              <p className="section-description">
                Glissez-déposez votre fichier Excel ou cliquez pour le
                sélectionner.
              </p>
              <ExcelDropzone onFileSelect={handleFileSelect} />
            </div>
          )}

          {/* Section 3 : Résultats */}
          {results && (
            <div className="import-section">
              <div className="section-header">
                <div className="section-number">
                  {results.success ? (
                    <FaCheckCircle style={{ color: "#22c55e" }} />
                  ) : (
                    <FaExclamationTriangle style={{ color: "#ef4444" }} />
                  )}
                </div>
                <h3>Résultats de l&apos;import</h3>
              </div>
              <ImportResultsDisplay
                results={results}
                onClose={handleCloseWithRefresh}
                entityLabel="opération"
              />
            </div>
          )}
        </div>

        {!results && (
          <div className="modal-import-footer">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Annuler
            </button>
            {file && (
              <button
                type="button"
                className="btn-import"
                onClick={handleImport}
                disabled={loading || !file}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    <span>Import en cours...</span>
                  </>
                ) : (
                  <>
                    <FaUpload />
                    <span>Importer</span>
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ModalImportBrouillard;
